import { useCurrentFrame, useVideoConfig, spring, interpolate } from 'remotion';

// Constants
import { ANIMATION_SPRING_CONFIGS, CLAMP_BOTH } from '@/constants';

// Types
import { AnimationPreset } from '@/types';

// Utils
import { combineClasses } from '@/utils';

export interface Flip3DBadgeProps {
  /** Frame number when animation should start */
  startFrame: number;
  /** Optional CSS class names */
  className?: string;
  /** Rotation angle after landing in degrees (default: -6) */
  finalRotation?: number;
  /** Whether to enable floating effect after landing (default: true) */
  enableFloat?: boolean;
}

/**
 * Flip3DBadge - Dramatic 3D Flip Entrance
 *
 * Animates a badge/container element with a 3D flip effect.
 * The element flips in from the back with elastic scaling
 * and an optional floating animation after landing.
 */
export const Flip3DBadge = ({
  startFrame,
  className = '',
  finalRotation = -6,
  enableFloat = true,
}: Flip3DBadgeProps): React.ReactNode => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const flipProgress = spring({
    frame: frame - startFrame,
    fps,
    config: ANIMATION_SPRING_CONFIGS[AnimationPreset.FLIP_3D],
  });

  // 3D flip from back
  const rotateY = interpolate(flipProgress, [0, 1], [-90, 0], CLAMP_BOTH);

  // Scale with overshoot
  const scaleX = interpolate(
    flipProgress,
    [0, 0.6, 0.8, 1],
    [0.1, 1.2, 1.1, 1],
    CLAMP_BOTH,
  );
  const scaleY = interpolate(
    flipProgress,
    [0, 0.5, 0.8, 1],
    [0.6, 1.1, 0.9, 1],
    CLAMP_BOTH,
  );

  // Opacity
  const opacity = interpolate(flipProgress, [0, 0.2], [0, 1], CLAMP_BOTH);

  // Subtle floating after landing
  const floatOffset =
    enableFloat && flipProgress > 0.9
      ? Math.sin((frame - startFrame) * 0.1) * 2
      : 0;

  return (
    <div
      className={combineClasses(
        className,
        'origin-left-center backface-visible',
      )}
      style={{
        transform: `perspective(1000px) rotateY(${rotateY}deg) scaleX(${scaleX}) scaleY(${scaleY}) translateY(${floatOffset}px) rotate(${finalRotation}deg)`,
        opacity,
      }}
    />
  );
};
